import { ExecutionContext, html, ref, repeat, when } from '@genesislcap/web-core';
import { customEvent } from '@genesislcap/foundation-events';
import {
  formatMillisecondsToMonthYear,
  formatSpread,
  formatToThreeDecimals,
  formatUstBenchmark,
  shouldShowPutDateColumn,
  OptionConfig,
  orderAmountFormatter,
  seriesSizeFormatter,
  isRetailTypeEditable,
} from '../deal-series.util';
import type { MaturityOrder } from '../deal-series.types';
import { formatDateOrBlank } from '../../deal-detail/deal-detail.util';
import {
  formatOrderAmount,
  getFlexMaturityDisplayValue,
  getOrderTypeDropdownValue,
  getPriorityTypeDropdownValue,
  getRetailTypeDropdownValue,
  normalizeNumericFieldForDisplay,
  orderTypeOptions,
  priorityTypeOptions,
  retailTypeOptions,
} from './deal-conditions-form.util';
import { DealConditionsForm } from './deal-conditions-form';
import { MunisMultiSelect } from '../munis-multi-select/munis-multi-select';
import { TaxStatusEnum } from '../../deals.types';

MunisMultiSelect;

interface LabelConfig {
  key: string;
  label: string;
  formatter: (val: string | number | null | undefined) => string;
  dataTestId: string;
  formatFromOrder?: (order: MaturityOrder | undefined) => string;
}

const baseLabelConfigs: LabelConfig[] = [
  {
    key: 'MATURITY_DATE',
    label: 'Maturity',
    formatter: (value) => {
      const { month, year } = formatMillisecondsToMonthYear(Number(value));
      return `${month}/${year}`;
    },
    dataTestId: 'order-conditions-maturity-date',
  },
  {
    key: 'PUT_DATE',
    label: 'Put Date',
    formatter: (value) => formatDateOrBlank(value),
    dataTestId: 'order-conditions-put-date',
  },
  {
    key: 'MATURITY_AMOUNT',
    label: 'Par Amount ($M)',
    formatter: orderAmountFormatter,
    dataTestId: 'order-conditions-maturity-amount',
  },
  {
    key: 'COUPON',
    label: 'Coupon',
    formatter: formatToThreeDecimals,
    dataTestId: 'order-conditions-coupon',
  },
  {
    key: 'YIELD',
    label: 'Yield',
    formatter: formatToThreeDecimals,
    dataTestId: 'order-conditions-yield',
  },
];

const spreadLabelConfig: LabelConfig = {
  key: 'SPREAD',
  label: 'Spread to MMD',
  formatter: formatSpread,
  dataTestId: 'order-conditions-spread',
};

const priceLabelConfig: LabelConfig = {
  key: 'PRICE',
  label: 'Price',
  formatter: formatToThreeDecimals,
  dataTestId: 'order-conditions-price',
};

const ustSpreadLabelConfig: LabelConfig = {
  key: 'TAX_TREASURY_SPREAD',
  label: 'Spread to UST',
  formatter: formatSpread,
  dataTestId: 'order-conditions-ust-spread',
};

const ustBenchmarkLabelConfig: LabelConfig = {
  key: 'UST_BENCHMARK',
  label: 'UST Benchmark',
  formatter: () => '',
  dataTestId: 'order-conditions-ust-benchmark',
  formatFromOrder: (order) =>
    formatUstBenchmark(order?.TAX_TREASURY_COUPON, order?.TAX_TREASURY_MATURITY_DATE),
};

const getEconomicsLabelConfigs = (taxStatus: TaxStatusEnum): LabelConfig[] => {
  if (taxStatus === TaxStatusEnum.TAXABLE) {
    return [priceLabelConfig, ustSpreadLabelConfig, ustBenchmarkLabelConfig];
  }
  return [spreadLabelConfig, priceLabelConfig];
};

const getVisibleLabelConfigs = (
  taxStatus: TaxStatusEnum,
  securityType: string | undefined,
  munisOrder: MaturityOrder | undefined,
): LabelConfig[] => {
  const configs = [...baseLabelConfigs, ...getEconomicsLabelConfigs(taxStatus)];
  return configs.filter(
    (config) =>
      config.key !== 'PUT_DATE' ||
      shouldShowPutDateColumn(securityType),
  );
};

export const dealConditionsFormTemplate = html<DealConditionsForm>`
  <div class="order-description">
    <div class="order-description-item text" data-test-id="order-conditions-series">
      <div class="label">Series</div>
      <div class="value">${(x) => seriesSizeFormatter(x.seriesCode, x.seriesSize)}</div>
    </div>
    ${repeat(
      (x) => getVisibleLabelConfigs(x.taxStatus, x.securityType, x.munisOrder),
      html<LabelConfig>`
        <div class="order-description-item" data-test-id="${(x) => x.dataTestId}">
          <div class="label">${(x) => x.label}</div>
          <div class="value">
            ${(x, ctx: ExecutionContext<DealConditionsForm>) =>
              x.formatFromOrder
                ? x.formatFromOrder(ctx.parent.munisOrder)
                : x.formatter(
                    ctx.parent.munisOrder?.[x.key as keyof MaturityOrder] as string | number | undefined,
                  )}
          </div>
        </div>
      `,
    )}
  </div>
  <div class="form">
    <div class="form-column">
      <div class="form-item">
        <div class="label">Order Amount ($M)</div>
        <div class="form-item-input ${(x) => {
          const validation = x.getQuantityValidation();
          return validation.hasError
            ? 'error'
            : validation.hasWarning
              ? 'warning'
              : '';
        }}">
          <rapid-text-field
            inputmode="numeric"
            data-test-id="order-conditions-input-order-amount"
            ?disabled="${(x) => x.isConditionsDisabled || x.isAllButtonActive || x.orderAmountLocked}"
            :value="${(x) => x.editingNumericField === 'QUANTITY'
              ? normalizeNumericFieldForDisplay(x.munisOrder?.QUANTITY, x.munisOrder)
              : formatOrderAmount(x.munisOrder, 'QUANTITY')}"
            @focus="${(x, ctx) => x.handleNumericFieldFocus('QUANTITY', customEvent(ctx))}"
            @blur="${(x, ctx) => x.handleNumericFieldBlur(customEvent(ctx))}"
            @change="${(x, ctx) => x.handleKeyup(customEvent(ctx), 'QUANTITY')}"
          ></rapid-text-field>
          <div class="error-message">${(x) => {
            const validation = x.getQuantityValidation();
            return validation.hasError || validation.hasWarning
              ? validation.message
              : '';
          }}</div>
        </div>
      </div>
      <div class="form-item">
        <div class="label">Minimum</div>
        <div class="form-item-input ${(x) => {
          const validation = x.getMinimumValidation();
          return validation.hasError ? 'error' : '';
        }}">
          <rapid-text-field
            inputmode="numeric"
            data-test-id="order-conditions-input-minimum"
            ?disabled="${(x) => x.isConditionsDisabled}"
            :value="${(x) => x.editingNumericField === 'MINIMUM_ORDER_AMOUNT'
              ? normalizeNumericFieldForDisplay(x.munisOrder?.MINIMUM_ORDER_AMOUNT)
              : formatOrderAmount(x.munisOrder, 'MINIMUM_ORDER_AMOUNT')}"
            @focus="${(x, ctx) => x.handleNumericFieldFocus('MINIMUM_ORDER_AMOUNT', customEvent(ctx))}"
            @blur="${(x, ctx) => x.handleNumericFieldBlur(customEvent(ctx))}"
            @change="${(x, ctx) => x.handleKeyup(customEvent(ctx), 'MINIMUM_ORDER_AMOUNT')}"
          ></rapid-text-field>
          <div class="error-message">${(x) => {
            const validation = x.getMinimumValidation();
            return validation.hasError ? validation.message : '';
          }}</div>
        </div>
      </div>
      <div class="form-item">
        <div class="label">Maximum</div>
        <div class="form-item-input ${(x) => {
          const validation = x.getMaximumValidation();
          return validation.hasError ? 'error' : '';
        }}">
          <rapid-text-field
            inputmode="numeric"
            data-test-id="order-conditions-input-maximum"
            ?disabled="${(x) => x.isConditionsDisabled}"
            :value="${(x) => x.editingNumericField === 'MAXIMUM_ORDER_AMOUNT'
              ? normalizeNumericFieldForDisplay(x.munisOrder?.MAXIMUM_ORDER_AMOUNT)
              : formatOrderAmount(x.munisOrder, 'MAXIMUM_ORDER_AMOUNT')}"
            @focus="${(x, ctx) => x.handleNumericFieldFocus('MAXIMUM_ORDER_AMOUNT', customEvent(ctx))}"
            @blur="${(x, ctx) => x.handleNumericFieldBlur(customEvent(ctx))}"
            @change="${(x, ctx) => x.handleKeyup(customEvent(ctx), 'MAXIMUM_ORDER_AMOUNT')}"
          ></rapid-text-field>
          <div class="error-message">${(x) => {
            const validation = x.getMaximumValidation();
            return validation.hasError ? validation.message : '';
          }}</div>
        </div>
      </div>
      <div class="form-item">
        <div class="label">AON</div>
        <div class="form-item-input">
          <rapid-checkbox
            data-test-id="order-conditions-input-aon"
            ?disabled="${(x) => x.isConditionsDisabled}"
            :checked="${(x) => !!x.munisOrder?.MUNI_AON}"
            @change="${(x, ctx) => x.handleKeyup(customEvent(ctx), 'MUNI_AON')}"
          ></rapid-checkbox>
        </div>
      </div>
      <div class="form-item">
        <div class="label">Order Type</div>
        <div class="form-item-input">
          <rapid-select
            data-test-id="order-conditions-input-order-type"
            ?disabled="${(x) => x.isConditionsDisabled}"
            :value="${(x) => getOrderTypeDropdownValue(x.munisOrder?.ORDER_TYPE)}"
            @change="${(x, ctx) => x.handleKeyup(customEvent(ctx), 'ORDER_TYPE')}"
          >
            ${repeat(
              () => orderTypeOptions,
              html<OptionConfig>`
                <rapid-option value="${(x) => x.value}">${(x) => x.displayValue}</rapid-option>
              `,
            )}
          </rapid-select>
        </div>
      </div>
      <div class="form-item">
        <div class="label">Retail Type</div>
        <div class="form-item-input">
          <rapid-select
            data-test-id="order-conditions-input-retail-type"
            ?disabled="${(x) => x.isConditionsDisabled || !isRetailTypeEditable(x.munisOrder)}"
            :value="${(x) => getRetailTypeDropdownValue(x.munisOrder?.RETAIL_TYPE)}"
            @change="${(x, ctx) => x.handleKeyup(customEvent(ctx), 'RETAIL_TYPE')}"
          >
            ${repeat(
              () => retailTypeOptions,
              html<OptionConfig>`
                <rapid-option value="${(x) => x.value}">${(x) => x.displayValue}</rapid-option>
              `,
            )}
          </rapid-select>
        </div>
      </div>
      <div class="form-item">
        <div class="label">Zip Code</div>
        <div class="form-item-input">
          <rapid-text-field
            data-test-id="order-conditions-input-zip"
            ?disabled="${(x) => x.isConditionsDisabled}"
            :value="${(x) => x.munisOrder?.ZIP_CODE ?? ''}"
            @change="${(x, ctx) => x.handleKeyup(customEvent(ctx), 'ZIP_CODE')}"
          ></rapid-text-field>
        </div>
      </div>
      <div class="form-item">
        <rapid-text-area
          rows="10"
          data-test-id="order-conditions-input-comments"
          ?disabled="${(x) => x.isConditionsDisabled}"
          maxlength="256"
          :value="${(x) => x.munisOrder?.COMMENT ?? ''}"
          @change="${(x, ctx) => x.handleKeyup(customEvent(ctx), 'COMMENT')}"
        >
          Comments
        </rapid-text-area>
      </div>
    </div>
    <div class="form-column">
      <div class="form-item">
        <div class="label">Strip</div>
        <div class="form-item-input">
          <rapid-checkbox
            data-test-id="order-conditions-input-strip"
            ?disabled="${(x) => x.isConditionsDisabled}"
            :checked="${(x) => !!x.munisOrder?.MUNI_STRIP}"
            @change="${(x, ctx) => x.handleKeyup(customEvent(ctx), 'MUNI_STRIP')}"
          ></rapid-checkbox>
        </div>
      </div>
      <div class="form-item">
        <div class="label">Flex Maturity</div>
        <div class="form-item-input flex-maturity-input" ${ref('flexMaturityField')}>
          <rapid-text-field
            data-test-id="order-conditions-input-flex-maturity"
            readonly
            ?disabled="${(x) => x.isConditionsDisabled}"
            :value="${(x) => getFlexMaturityDisplayValue(x.munisOrder?.FLEX_MATURITY, x.flexMaturityOptions)}"
            @click="${(x) => x.handleFlexMaturityFocus()}"
            @focus="${(x) => x.handleFlexMaturityFocus()}"
          ></rapid-text-field>
          ${when(x => x.flexMaturityActive, html<DealConditionsForm>`
            <munis-multi-select
              data-test-id="order-conditions-input-flex-maturity-dropdown"
              issuance-key="${(x) => x.munisOrder?.MATURITY_ID}"
              key="FLEX_MATURITY"
              :munisOrders="${(x) => x.flexMaturityOptions}"
              :flexMaturityKey="${(x) => x.munisOrder?.FLEX_MATURITY || ''}"
              @selected-changed="${(x, ctx) => x.handleFlexMaturityChanged(customEvent(ctx))}"
              @close="${(x) => x.handleFlexMaturityClose()}"
            ></munis-multi-select>
            `)}
        </div>
      </div>
      <div class="form-item">
        <div class="label">Flex Coupon</div>
        <div class="form-item-input">
          <rapid-number-field
            data-test-id="order-conditions-input-flex-coupon"
            hideStep
            ?disabled="${(x) => x.isConditionsDisabled}"
            :value="${(x) => x.munisOrder?.FLEX_COUPON ? formatToThreeDecimals(x.munisOrder.FLEX_COUPON) : ''}"
            @change="${(x, ctx) => x.handleKeyup(customEvent(ctx), 'FLEX_COUPON')}"
          ></rapid-number-field>
        </div>
      </div>
      <div class="form-item">
        <div class="label">Priority Type</div>
        <div class="form-item-input">
          <rapid-select
            data-test-id="order-conditions-input-priority-type"
            ?disabled="${(x) => x.isConditionsDisabled}"
            :value="${(x) => getPriorityTypeDropdownValue(x.munisOrder?.PRIORITY_TYPE)}"
            @change="${(x, ctx) => x.handleKeyup(customEvent(ctx), 'PRIORITY_TYPE')}"
          >
            ${repeat(
              () => priorityTypeOptions,
              html<OptionConfig>`
                <rapid-option value="${(x) => x.value}">${(x) => x.displayValue}</rapid-option>
              `,
            )}
          </rapid-select>
        </div>
      </div>
    </div>
  </div>
`;
